import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCockpitData } from "../hooks/useCockpitData";
import NavRail from "../components/NavRail";
import { triggerPanic } from "../services/api";

/* ============================================================
   COCKPIT — Backoffice surveillance bot memecoin Solana (V1)
   Câblé sur les vraies données (useCockpitData). Dark terminal.
   ============================================================ */

const ERR_THRESHOLD = 10; // seuil visuel "hot" / sell-storm (le backend renvoie les counts)

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{
  --bg:#0a0b0e; --surface:#14161c; --surface2:#191c23; --border:#1f2329;
  --text:#e6e8ec; --text2:#8a8f98;
  --green:#00e08a; --red:#ff3b5c; --amber:#ffb020; --cyan:#22d3ee;
}
.ck, .ck *{font-family:'JetBrains Mono', ui-monospace, monospace;box-sizing:border-box;}
.ck{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:17px;border:none;background:transparent;}
.sb-item:hover{background:var(--surface);color:var(--text);}
.sb-item.on{background:var(--surface);color:var(--green);box-shadow:inset 2px 0 0 var(--green);}
.sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.sb-item:hover .tip{opacity:1;}
.sb-spacer{flex:1;}
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.mode{display:flex;align-items:center;gap:9px;padding:9px 16px;border-radius:9px;font-weight:800;font-size:14px;letter-spacing:.04em;}
.mode.live{background:var(--red);color:#fff;box-shadow:0 0 0 1px rgba(255,59,92,.6),0 0 22px rgba(255,59,92,.35);}
.mode.paper{background:rgba(0,224,138,.13);color:var(--green);border:1px solid rgba(0,224,138,.4);}
.dot{width:9px;height:9px;border-radius:50%;background:#fff;animation:pulse 1.3s infinite;}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.7);}70%{box-shadow:0 0 0 7px rgba(255,255,255,0);}100%{box-shadow:0 0 0 0 rgba(255,255,255,0);}}
.mode.paper .dot{background:var(--green);animation:pulseg 2.4s infinite;}
@keyframes pulseg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.wallet{display:flex;flex-direction:column;gap:1px;}
.wallet .lbl{font-size:10px;color:var(--text2);letter-spacing:.12em;text-transform:uppercase;}
.wallet .val{font-size:22px;font-weight:700;letter-spacing:-.01em;}
.delta{display:flex;align-items:center;gap:5px;font-size:13px;font-weight:600;}
.delta.neg{color:var(--red);} .delta.pos{color:var(--green);}
.top-spacer{flex:1;}
.clock{font-size:11px;color:var(--text2);text-align:right;}
.clock b{color:var(--text);font-weight:600;}
.panic{display:flex;align-items:center;gap:8px;background:var(--red);color:#fff;border:none;border-radius:9px;padding:11px 18px;font-weight:800;font-size:13px;letter-spacing:.03em;cursor:pointer;font-family:inherit;box-shadow:0 0 0 1px rgba(255,59,92,.7),0 6px 22px rgba(255,59,92,.4);transition:transform .1s,box-shadow .15s;}
.panic:hover{transform:translateY(-1px);box-shadow:0 0 0 1px rgba(255,59,92,.9),0 8px 30px rgba(255,59,92,.55);}
.panic:active{transform:translateY(0);}
.panic.disabled{background:var(--surface);color:var(--text2);box-shadow:none;cursor:not-allowed;border:1px solid var(--border);}
.grid{flex:1;overflow:auto;padding:14px;display:grid;gap:12px;grid-template-columns:2fr 1fr 1fr;grid-template-rows:auto auto 1fr;grid-template-areas:"errors errors daily" "errors errors kill" "stats perf pos";}
@media(max-width:1080px){.grid{grid-template-columns:1fr 1fr;grid-template-areas:"errors errors" "daily kill" "stats perf" "pos pos";}}
@media(max-width:680px){.grid{grid-template-columns:1fr;grid-template-areas:"errors""daily""kill""stats""perf""pos";}}
.card{background:var(--surface);border:1px solid var(--border);border-radius:11px;padding:13px 14px;display:flex;flex-direction:column;min-width:0;}
.card-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;}
.card-ti{font-size:11px;color:var(--text2);letter-spacing:.14em;text-transform:uppercase;font-weight:600;}
.card-meta{font-size:10px;color:var(--text2);}
.c-errors{grid-area:errors;} .c-daily{grid-area:daily;} .c-kill{grid-area:kill;}
.c-stats{grid-area:stats;} .c-perf{grid-area:perf;} .c-pos{grid-area:pos;}
.storm{display:flex;align-items:center;gap:8px;background:rgba(255,59,92,.14);border:1px solid rgba(255,59,92,.5);color:var(--red);font-weight:800;font-size:12px;letter-spacing:.05em;padding:8px 12px;border-radius:8px;margin-bottom:11px;animation:stormpulse 1s infinite;}
@keyframes stormpulse{0%,100%{background:rgba(255,59,92,.10);}50%{background:rgba(255,59,92,.22);}}
.paper-note{font-size:11.5px;color:var(--text2);padding:14px 6px;text-align:center;border:1px dashed var(--border);border-radius:8px;}
.err-tbl{display:flex;flex-direction:column;gap:2px;}
.err-row{display:grid;grid-template-columns:62px 1fr 62px 96px;align-items:center;gap:10px;padding:8px 10px;border-radius:7px;background:var(--surface2);border:1px solid transparent;}
.err-row.hot{border-color:rgba(255,59,92,.55);animation:rowblink 1.05s infinite;}
@keyframes rowblink{0%,100%{background:var(--surface2);}50%{background:rgba(255,59,92,.16);}}
.err-code{font-weight:700;font-size:13px;color:var(--text);}
.err-row.hot .err-code{color:var(--red);}
.err-name{font-size:11.5px;color:var(--text2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.err-cnt{font-size:20px;font-weight:800;text-align:right;letter-spacing:-.02em;}
.err-cnt.hot{color:var(--red);} .err-cnt.zero{color:var(--green);} .err-cnt.warm{color:var(--amber);}
.gauge-top{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:9px;}
.gauge-val{font-size:18px;font-weight:700;}
.gauge-val .of{color:var(--text2);font-size:13px;font-weight:500;}
.gauge-pct{font-size:12px;font-weight:700;}
.bar{height:13px;border-radius:7px;background:#0a0b0e;border:1px solid var(--border);overflow:hidden;position:relative;}
.bar-fill{height:100%;border-radius:6px;transition:width .5s ease,background .3s;}
.gauge-sub{margin-top:8px;font-size:10.5px;color:var(--text2);}
.gauge-sub b{color:var(--text);}
.live-only{font-size:9px;color:var(--amber);letter-spacing:.04em;text-transform:uppercase;background:rgba(255,176,32,.12);border:1px solid rgba(255,176,32,.3);padding:2px 7px;border-radius:10px;font-weight:600;}
.kill-list{display:flex;flex-direction:column;gap:9px;}
.kill-item{display:flex;align-items:center;justify-content:space-between;padding:9px 11px;border-radius:8px;background:var(--surface2);}
.kill-name{font-size:11px;color:var(--text2);}
.kill-name b{color:var(--text);font-weight:600;}
.pill{display:flex;align-items:center;gap:6px;font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;}
.pill.off{background:rgba(138,143,152,.14);color:var(--text2);}
.pill.on{background:rgba(255,59,92,.16);color:var(--red);}
.pill.on .pd{width:7px;height:7px;border-radius:50%;background:var(--red);animation:pulsr 1s infinite;}
@keyframes pulsr{0%,100%{opacity:1;}50%{opacity:.3;}}
.pill.warm{background:rgba(255,176,32,.15);color:var(--amber);}
.ratio{font-size:16px;font-weight:800;}
.stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;}
.stat{display:flex;flex-direction:column;gap:2px;padding:8px 10px;background:var(--surface2);border-radius:7px;}
.stat .k{font-size:10px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;}
.stat .v{font-size:17px;font-weight:700;}
.perf-sub{font-size:11px;color:var(--text2);margin-top:8px;}
.perf-sub b.neg{color:var(--red);} .perf-sub b.pos{color:var(--green);}
.pos-count{font-size:11px;color:var(--text2);margin-bottom:9px;}
.pos-count b{color:var(--text);font-weight:700;font-size:13px;}
.pos-row{display:grid;grid-template-columns:1fr auto auto auto;gap:9px;align-items:center;padding:9px 11px;background:var(--surface2);border-radius:8px;font-size:12px;margin-bottom:5px;}
.pos-tk{font-weight:700;}
.pos-pnl.pos{color:var(--green);font-weight:700;} .pos-pnl.neg{color:var(--red);font-weight:700;}
.pos-sz{color:var(--text2);} .pos-t{color:var(--text2);}
.pos-link{margin-top:6px;font-size:11px;color:var(--cyan);cursor:pointer;}
.pos-link:hover{text-decoration:underline;}
.footer{flex:0 0 auto;border-top:1px solid var(--border);background:#0c0d11;padding:8px 18px;display:flex;gap:26px;flex-wrap:wrap;font-size:11px;color:var(--text2);}
.footer b{color:var(--text);font-weight:600;} .footer .g{color:var(--green);font-weight:600;} .footer .r{color:var(--red);font-weight:600;}
.flash{animation:flash .35s ease;border-radius:4px;}
@keyframes flash{0%{background:rgba(34,211,238,.35);}100%{background:transparent;}}
.flash-up{animation:flashup .4s ease;border-radius:4px;}
@keyframes flashup{0%{background:rgba(255,59,92,.4);}100%{background:transparent;}}
.ovl{position:fixed;inset:0;background:rgba(0,0,0,.72);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:100;animation:fade .15s;}
@keyframes fade{from{opacity:0;}to{opacity:1;}}
.modal{width:440px;max-width:92vw;background:var(--surface);border:1px solid var(--red);border-radius:13px;padding:22px;box-shadow:0 0 0 1px rgba(255,59,92,.4),0 20px 60px rgba(0,0,0,.6);animation:pop .18s;}
@keyframes pop{from{transform:scale(.94);opacity:0;}to{transform:scale(1);opacity:1;}}
.modal h2{font-size:18px;font-weight:800;color:var(--red);margin-bottom:12px;display:flex;align-items:center;gap:8px;}
.modal p{font-size:13px;color:var(--text);line-height:1.55;margin-bottom:6px;}
.modal .warn{font-size:12px;color:var(--amber);margin-bottom:18px;}
.modal-btns{display:flex;gap:10px;}
.btn{flex:1;padding:12px;border-radius:9px;font-weight:700;font-size:13px;cursor:pointer;border:none;font-family:inherit;transition:filter .12s;}
.btn:hover{filter:brightness(1.12);}
.btn.cancel{background:var(--surface2);color:var(--text);border:1px solid var(--border);}
.btn.confirm{background:var(--red);color:#fff;box-shadow:0 0 18px rgba(255,59,92,.4);}
.toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);color:#04150d;font-weight:800;font-size:13px;padding:12px 22px;border-radius:10px;z-index:110;animation:toastin .3s;}
.toast.ok{background:var(--green);box-shadow:0 10px 34px rgba(0,224,138,.4);}
.toast.err{background:var(--amber);box-shadow:0 10px 34px rgba(255,176,32,.4);}
@keyframes toastin{from{transform:translate(-50%,16px);opacity:0;}to{transform:translate(-50%,0);opacity:1;}}
`;

function useFlash(value, dir = "neutral") {
  const [cls, setCls] = useState("");
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      setCls(dir === "up" ? "flash-up" : "flash");
      prev.current = value;
      const t = setTimeout(() => setCls(""), 400);
      return () => clearTimeout(t);
    }
  }, [value, dir]);
  return cls;
}
function FlashNum({ value, dir, className = "", style }) {
  const f = useFlash(value, dir);
  return <span className={`${className} ${f}`} style={style}>{value}</span>;
}
function Sparkline({ data, color, width = 96, height = 30, fill = true }) {
  if (!data || data.length < 2) return <svg width={width} height={height} />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const n = data.length;
  const pts = data.map((v, i) => {
    const x = (i / (n - 1)) * (width - 2) + 1;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return [x, y];
  });
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `1,${height - 1} ${line} ${width - 1},${height - 1}`;
  const last = pts[pts.length - 1];
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {fill && <polygon points={area} fill={color} opacity="0.12" />}
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill={color} />
    </svg>
  );
}

const fmtSol = (v, d = 4) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));
const fmtHold = (s) => (s == null ? "—" : `${Math.floor(s / 60)}m${String(Math.floor(s % 60)).padStart(2, "0")}`);
const fmtUsd = (v) => (v == null ? "—" : (v >= 0 ? "+" : "") + "$" + Math.round(v).toLocaleString("en-US"));

export default function CockpitPage() {
  const navigate = useNavigate();
  const { risk, errors, instant, wallet, stats, positions } = useCockpitData();
  const [clock, setClock] = useState(new Date());
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const r = risk.data || {};
  const mode = (r.mode || "paper").toUpperCase();
  const isLive = mode === "LIVE";
  const errData = errors.data || { paper: true, errors: [] };
  const errList = errData.errors || [];
  const storm = isLive && errList.some((e) => e.count > ERR_THRESHOLD);

  const dailyLoss = r.daily_loss_sol ?? 0;
  const dailyCap = r.daily_cap_sol ?? 0.4;
  const dailyPct = dailyCap > 0 ? Math.min(100, (dailyLoss / dailyCap) * 100) : 0;
  const gaugeColor = dailyPct > 80 ? "var(--red)" : dailyPct > 50 ? "var(--amber)" : "var(--green)";

  const killArmed = r.kill_buys?.armed;
  const stopSells = r.stop_sells?.armed;
  const anyArmed = killArmed || stopSells;
  const consec = r.consecutive_losses ?? 0;
  const sf1 = r.sf1_threshold ?? 3;

  const inst = instant.data || {};
  const w = wallet.data || {};
  const cp = stats.data?.closed_positions || {};
  const activeCount = positions.data?.total ?? stats.data?.active_positions?.count ?? 0;
  const firstPos = positions.data?.positions?.[0];

  const doPanic = useCallback(async (action) => {
    setModal(false);
    try {
      await triggerPanic(action);
      const okMsg = action === "disarm"
        ? "✓ désarmé — kill:buys & kill:sells à 0"
        : "✓ ARMÉ — kill:buys + kill:sells coupés";
      setToast({ kind: "ok", msg: okMsg });
      risk.refetch();
    } catch (e) {
      const status = e?.response?.status;
      setToast({
        kind: "err",
        msg: status === 404 ? "PANIC pas encore actif côté serveur" :
             status === 403 ? "Réservé aux admins" : "Échec PANIC — voir logs",
      });
    }
    setTimeout(() => setToast(null), 4200);
  }, [risk]);

  const t = clock.toLocaleTimeString("fr-FR", { hour12: false, timeZone: "Europe/Paris" });

  return (
    <div className="ck">
      <style>{CSS}</style>

      <NavRail />

      <div className="main">
        <div className="top">
          {isLive ? (
            <div className="mode live"><span className="dot" /> LIVE — CAPITAL RÉEL</div>
          ) : (
            <div className="mode paper"><span className="dot" /> PAPER</div>
          )}

          <div className="wallet">
            <span className="lbl">Wallet</span>
            <FlashNum className="val" value={w.configured === false ? "non config." : `${fmtSol(w.sol)} SOL`} />
          </div>

          {w.session_delta_sol != null && (
            <div className={`delta ${w.session_delta_sol < 0 ? "neg" : "pos"}`}>
              {w.session_delta_sol < 0 ? "▼" : "▲"}
              <FlashNum value={`${w.session_delta_sol >= 0 ? "+" : ""}${fmtSol(w.session_delta_sol)} SOL`} />
              <span style={{ color: "var(--text2)", fontWeight: 400, fontSize: 10 }}>session</span>
            </div>
          )}

          <div className="top-spacer" />
          <div className="clock"><b>{t}</b><br />Europe/Paris</div>

          <button
            className="panic"
            onClick={() => setModal(true)}
            title="Kill-switches — armer / désarmer"
          >
            ⏻ {anyArmed ? "ARMÉ — gérer" : "STOP"}
          </button>
        </div>

        <div className="grid">
          {/* 1 · ON-CHAIN ERRORS */}
          <div className="card c-errors">
            <div className="card-hd">
              <span className="card-ti">On-chain errors · 5 min</span>
              <span className="card-meta">{errors.isError ? "indispo" : errors.isFetching ? "…" : "live"}</span>
            </div>
            {storm && <div className="storm">⚠ SELL-STORM DÉTECTÉ — codes au-dessus du seuil</div>}
            {errData.paper ? (
              <div className="paper-note">Mode PAPER — moniteur inactif (les TX paper ne touchent pas la chaîne).</div>
            ) : errList.length === 0 ? (
              <div className="paper-note">Aucune erreur on-chain sur la fenêtre. ✓</div>
            ) : (
              <div className="err-tbl">
                {errList.map((e) => {
                  const hot = e.count > ERR_THRESHOLD;
                  const cntCls = hot ? "hot" : e.count === 0 ? "zero" : e.count > ERR_THRESHOLD * 0.6 ? "warm" : "";
                  const sc = hot ? "#ff3b5c" : e.count === 0 ? "#00e08a" : e.count > ERR_THRESHOLD * 0.6 ? "#ffb020" : "#8a8f98";
                  return (
                    <div key={e.code} className={`err-row ${hot ? "hot" : ""}`}>
                      <span className="err-code">{e.code}</span>
                      <span className="err-name">{e.name}</span>
                      <FlashNum className={`err-cnt ${cntCls}`} value={e.count} dir={hot ? "up" : "neutral"} />
                      <Sparkline data={e.spark} color={sc} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 2 · DAILY LOSS */}
          <div className="card c-daily">
            <div className="card-hd">
              <span className="card-ti">Perte du jour</span>
              {isLive
                ? <span className="gauge-pct" style={{ color: gaugeColor }}>{dailyPct.toFixed(0)}%</span>
                : <span className="live-only">métrique live — inactif en paper</span>}
            </div>
            <div className="gauge-top">
              <span className="gauge-val">{fmtSol(dailyLoss, 3)} <span className="of">/ {fmtSol(dailyCap, 3)} SOL</span></span>
            </div>
            <div className="bar"><div className="bar-fill" style={{ width: `${dailyPct}%`, background: gaugeColor }} /></div>
            <div className="gauge-sub">cap <b>MAX_DAILY_LOSS_SOL = {fmtSol(dailyCap, 2)}</b> · marge {fmtSol(Math.max(0, dailyCap - dailyLoss), 3)} SOL</div>
          </div>

          {/* 3 · KILL SWITCHES */}
          <div className="card c-kill">
            <div className="card-hd"><span className="card-ti">Kill switches</span></div>
            <div className="kill-list">
              <div className="kill-item">
                <span className="kill-name"><b>kill:buys</b><br />achats — actif tous modes</span>
                {killArmed ? <span className="pill on"><span className="pd" /> ACHATS COUPÉS</span> : <span className="pill off">armé : non</span>}
              </div>
              <div className="kill-item">
                <span className="kill-name"><b>kill:sells</b><br />frein anti-storm</span>
                {stopSells ? <span className="pill on"><span className="pd" /> VENTES STOPPÉES</span> : <span className="pill off">armé : non</span>}
              </div>
              <div className="kill-item">
                <span className="kill-name"><b>SF-1</b><br />pertes consécutives{!isLive && <><br /><span className="live-only">compteur live-only · kill:buys global (paper inclus)</span></>}</span>
                <span className={`pill ${consec >= sf1 ? "on" : consec > 0 ? "warm" : "off"}`}><span className="ratio">{consec} / {sf1}</span></span>
              </div>
            </div>
          </div>

          {/* 4 · INSTANT STATS */}
          <div className="card c-stats">
            <div className="card-hd"><span className="card-ti">Stats instantanées</span></div>
            <div className="stats-grid">
              <div className="stat"><span className="k">buys / min</span><span className="v">{inst.buys_per_min ?? "—"}</span></div>
              <div className="stat"><span className="k">sells ok · 5m</span><span className="v">{inst.sells_ok_5m ?? "—"}</span></div>
              <div className="stat"><span className="k">send latency</span><span className="v">{inst.send_latency_ms ?? "—"} ms</span></div>
              <div className="stat"><span className="k">positions</span><span className="v">{activeCount}</span></div>
            </div>
          </div>

          {/* 5 · PERF GLOBALE */}
          <div className="card c-perf">
            <div className="card-hd"><span className="card-ti">Performance globale</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "center", flex: 1 }}>
              <div className="stat"><span className="k">realized PnL</span>
                <span className="v" style={{ color: (cp.realized_pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{fmtUsd(cp.realized_pnl)}</span>
              </div>
              <div className="perf-sub">win rate <b className={cp.win_rate >= 50 ? "pos" : "neg"}>{cp.win_rate ?? "—"}%</b> · {cp.realized_pnl_percent ?? "—"}%</div>
            </div>
          </div>

          {/* 6 · POSITIONS OUVERTES */}
          <div className="card c-pos">
            <div className="card-hd"><span className="card-ti">Positions ouvertes</span></div>
            <div className="pos-count">Open : <b>{activeCount}</b></div>
            {firstPos ? (
              <div className="pos-row">
                <span className="pos-tk">{firstPos.token_symbol || firstPos.token_name || "?"}</span>
                <span className={`pos-pnl ${(firstPos.pnl_percent ?? 0) >= 0 ? "pos" : "neg"}`}>
                  {(firstPos.pnl_percent ?? 0) >= 0 ? "+" : ""}{(firstPos.pnl_percent ?? 0).toFixed(1)}%
                </span>
                <span className="pos-sz">{fmtSol(firstPos.initial_amount ?? firstPos.amount, 3)} SOL</span>
                <span className="pos-t">⏱ {fmtHold(firstPos.holding_duration?.total_seconds)}</span>
              </div>
            ) : (
              <div className="paper-note">Aucune position ouverte.</div>
            )}
            <div className="pos-link" onClick={() => navigate("/positions")}>→ voir toutes</div>
          </div>
        </div>

        <div className="footer">
          <span>Closed : <b>{(cp.count ?? 0).toLocaleString("fr-FR")}</b></span>
          <span>Win rate : <b>{cp.win_rate ?? "—"}%</b></span>
          <span>Realized PnL : <span className={(cp.realized_pnl ?? 0) >= 0 ? "g" : "r"}>{fmtUsd(cp.realized_pnl)}</span></span>
          <span>Active : <b>{activeCount}</b></span>
        </div>
      </div>

      {modal && (
        <div className="ovl" onClick={() => setModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>⏻ ARRÊT D'URGENCE</h2>
            <p>Armer <b>kill:buys</b> (stoppe les achats) <b>+ kill:sells</b> (frein anti-storm : le chemin de vente s'arrête).</p>
            <p className="warn">
              État — kill:buys : {killArmed ? "ARMÉ" : "off"} · kill:sells : {stopSells ? "ARMÉ" : "off"}.
              Le désarmement est manuel, quand c'est sous contrôle.
            </p>
            <div className="modal-btns">
              <button className="btn cancel" onClick={() => setModal(false)}>Annuler</button>
              {anyArmed && <button className="btn cancel" onClick={() => doPanic("disarm")}>Désarmer tout</button>}
              <button className="btn confirm" onClick={() => doPanic("arm_all")}>STOP — armer tout</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
    </div>
  );
}
